import { useTranslation } from 'react-i18next'
import { Package, AlertCircle } from 'lucide-react'
import type { ProcessableCollection } from '../../../types/ingestion'

interface ProcessableCollectionsSectionProps {
  collections: ProcessableCollection[]
  loading: boolean
  error: string | null
  isRowBusy: (receiptId: string) => boolean
  onProcess: (receiptId: string) => void
  onTriage: (receiptId: string) => void
}

const STATUS_COLOURS: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
}

const DEFAULT_STATUS_COLOUR = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'

function EmptyState(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="py-10 text-center">
      <Package className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        {t('detail.processing.noCollectionsAvailable')}
      </p>
    </div>
  )
}

function ErrorState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 py-6 text-red-600" role="alert">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  )
}

function CollectionsTableHead(): React.ReactElement {
  const { t } = useTranslation('cases')
  const translate = t as (key: string) => string
  const headers: Array<[string, string]> = [
    ['evidenceNumber', 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'],
    ['hostname', 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'],
    ['receivedAt', 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'],
    ['filename', 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'],
    ['status', 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'],
    ['action', 'py-2 text-right font-medium text-slate-600 dark:text-slate-400'],
  ]
  return (
    <thead>
      <tr className="border-b border-slate-200 dark:border-slate-700">
        {headers.map(([key, className]) => (
          <th key={key} className={className}>
            {translate(`detail.processing.${key}`)}
          </th>
        ))}
      </tr>
    </thead>
  )
}

interface CollectionRowProps {
  col: ProcessableCollection
  busy: boolean
  onProcess: (receiptId: string) => void
  onTriage: (receiptId: string) => void
}

function CollectionRow({ col, busy, onProcess, onTriage }: CollectionRowProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <tr key={col.receiptId} className="border-b border-slate-100 dark:border-slate-800">
      <td className="py-2 pr-4 font-mono text-xs text-slate-900 dark:text-slate-100">
        {col.evidenceNumber}
      </td>
      <td className="py-2 pr-4 text-slate-700 dark:text-slate-300">{col.endpointHostname}</td>
      <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">
        {new Date(col.receivedAt).toLocaleDateString()}
      </td>
      <td className="max-w-xs truncate py-2 pr-4 font-mono text-xs text-slate-500 dark:text-slate-400">
        {col.packageFilename}
      </td>
      <td className="py-2 pr-4">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOURS[col.ingestionStatus] ?? DEFAULT_STATUS_COLOUR}`}
        >
          {col.ingestionStatus}
        </span>
      </td>
      <td className="py-2 text-right">
        <div className="inline-flex items-center gap-2">
          {col.canProcess ? (
            <button
              onClick={() => {
                onProcess(col.receiptId)
              }}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors disabled:opacity-50"
              style={{ background: 'var(--accent, #4e5e82)' }}
            >
              {busy ? t('detail.processing.processing') : t('detail.processing.process')}
            </button>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {t('detail.processing.cannotProcess')}
            </span>
          )}
          <button
            onClick={() => {
              onTriage(col.receiptId)
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {t('detail.processing.artefactTriage')}
          </button>
        </div>
      </td>
    </tr>
  )
}

export function ProcessableCollectionsSection({
  collections,
  loading,
  error,
  isRowBusy,
  onProcess,
  onTriage,
}: ProcessableCollectionsSectionProps): React.ReactElement {
  const { t } = useTranslation('cases')

  if (loading) {
    return (
      <div
        className="py-8 text-center text-sm text-slate-500 dark:text-slate-400"
        role="status"
        aria-busy="true"
      >
        {t('detail.processing.processing')}
      </div>
    )
  }
  if (error) return <ErrorState message={error} />
  if (collections.length === 0) return <EmptyState />

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <CollectionsTableHead />
        <tbody>
          {collections.map((col) => (
            <CollectionRow
              key={col.receiptId}
              col={col}
              busy={isRowBusy(col.receiptId)}
              onProcess={onProcess}
              onTriage={onTriage}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
